// jobFormDefaults.js
export const DEFAULT_JOB_FORM = {
  title: '',
  prompt: '',
  cron: '0 6 * * *',
  recipients: [],
};

// Helper: returns a *fresh* copy so arrays aren't shared
export function createEmptyJobForm() {
  return {
    title: '',
    prompt: '',
    cron: '0 6 * * *',
    recipients: [],
  };
}

// ----- Timezone helpers -----
export function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Internal: construct a local Date for a specific Y-M-D h:m in the user's environment
function buildLocalDate(year, monthIndex, day, hour, minute) {
  return new Date(year, monthIndex, day, hour, minute, 0, 0);
}

// Internal: given a local time (hour, minute) and a local reference date,
// return the corresponding [utcMinute, utcHour, utcDateObj]
function localToUtcFields(hourLocal, minuteLocal, localRefDate) {
  const dtLocal = buildLocalDate(
      localRefDate.getFullYear(),
      localRefDate.getMonth(),
      localRefDate.getDate(),
      Number(hourLocal),
      Number(minuteLocal),
  );
  return [dtLocal.getUTCMinutes(), dtLocal.getUTCHours(), dtLocal];
}

// Internal: given a UTC time (hour, minute) and a UTC reference date,
// return the corresponding [localMinute, localHour, localDateObj]
function utcToLocalFields(hourUtc, minuteUtc, utcRefDate) {
  // Create a Date whose components we set in UTC, then read local.
  const dtUtc = new Date(Date.UTC(
      utcRefDate.getUTCFullYear(),
      utcRefDate.getUTCMonth(),
      utcRefDate.getUTCDate(),
      Number(hourUtc),
      Number(minuteUtc),
      0,
      0,
  ));
  return [dtUtc.getMinutes(), dtUtc.getHours(), dtUtc];
}

// Convert a cron string the user selected in LOCAL time to an equivalent UTC cron string.
// Supports:
// - daily: "m h * * *"
// - weekly: "m h * * wd[,wd...]"
// - monthly: "m h d * *" (with numeric day; "L" is passed through unchanged)
export function convertLocalCronToUtc(localCron) {
  if (!localCron || typeof localCron !== 'string') return localCron;
  const parts = localCron.trim().split(/\s+/);
  if (parts.length !== 5) return localCron;
  let [m, h, d, mo, wd] = parts;

  const todayLocal = new Date();

  // Daily
  if (d === '*' && wd === '*') {
    const [mUtc, hUtc] = localToUtcFields(h, m, todayLocal);
    return [String(mUtc), String(hUtc), '*', mo, '*'].join(' ');
  }

  // Weekly
  if (wd !== '*') {
    const minuteLocal = Number(m);
    const hourLocal = Number(h);
    const weekdaysLocal = String(wd).split(',').map(s => s.trim()).filter(Boolean);

    // Map each local weekday to its UTC weekday using the next occurrence from today.
    const mappedUtcWeekdays = new Set();
    let hourUtcRef = null;
    let minuteUtcRef = null;
    for (const wdLocalStr of weekdaysLocal) {
      const wdLocal = Number(wdLocalStr); // 0=Sun..6=Sat
      const delta = (wdLocal - todayLocal.getDay() + 7) % 7;
      const refLocal = buildLocalDate(
          todayLocal.getFullYear(),
          todayLocal.getMonth(),
          todayLocal.getDate() + delta,
          hourLocal,
          minuteLocal,
      );
      const utcWd = refLocal.getUTCDay(); // 0..6
      mappedUtcWeekdays.add(String(utcWd));
      const mU = refLocal.getUTCMinutes();
      const hU = refLocal.getUTCHours();
      if (hourUtcRef === null) hourUtcRef = hU;
      if (minuteUtcRef === null) minuteUtcRef = mU;
    }
    const wdFieldUtc = Array.from(mappedUtcWeekdays).sort((a, b) => Number(a) - Number(b)).join(',');
    return [String(minuteUtcRef ?? 0), String(hourUtcRef ?? 0), '*', mo, wdFieldUtc].join(' ');
  }

  // Monthly (numeric day only)
  if (d !== '*' && d !== 'L') {
    const minuteLocal = Number(m);
    const hourLocal = Number(h);
    const dayLocal = Number(d);
    // Use current month as the reference for mapping.
    const refLocal = buildLocalDate(
        todayLocal.getFullYear(),
        todayLocal.getMonth(),
        dayLocal,
        hourLocal,
        minuteLocal,
    );
    const mUtc = refLocal.getUTCMinutes();
    const hUtc = refLocal.getUTCHours();
    const dayUtc = refLocal.getUTCDate();
    return [String(mUtc), String(hUtc), String(dayUtc), mo, '*'].join(' ');
  }

  // "L" or anything else: pass-through (note: backend may not support "L")
  return localCron;
}

// Convert a UTC cron string from the backend into an approximate local-time equivalent
// for display/editing in the picker. Same caveats as above.
export function convertUtcCronToLocal(utcCron) {
  if (!utcCron || typeof utcCron !== 'string') return utcCron;
  const parts = utcCron.trim().split(/\s+/);
  if (parts.length !== 5) return utcCron;
  let [m, h, d, mo, wd] = parts;

  const todayUtc = new Date(); // acts as reference; we'll read/set UTC components

  // Daily
  if (d === '*' && wd === '*') {
    const [mLocal, hLocal] = utcToLocalFields(h, m, todayUtc);
    return [String(mLocal), String(hLocal), '*', mo, '*'].join(' ');
  }

  // Weekly
  if (wd !== '*') {
    const minuteUtc = Number(m);
    const hourUtc = Number(h);
    // Expand ranges like "1-5" into ["1","2","3","4","5"], keep lists intact
    const tokens = String(wd).split(',').map(s => s.trim()).filter(Boolean);
    const weekdaysUtc = [];
    for (const tok of tokens) {
      if (tok.includes('-')) {
        const [startStr, endStr] = tok.split('-');
        const start = Number(startStr);
        const end = Number(endStr);
        if (!Number.isNaN(start) && !Number.isNaN(end)) {
          if (start <= end) {
            for (let v = start; v <= end; v++) weekdaysUtc.push(String(v));
          } else {
            // Handle wrap-around ranges like "5-1" → 5,6,0,1
            for (let v = start; v <= 6; v++) weekdaysUtc.push(String(v));
            for (let v = 0; v <= end; v++) weekdaysUtc.push(String(v));
          }
        }
      } else {
        weekdaysUtc.push(tok);
      }
    }

    const mappedLocalWeekdays = new Set();
    let hourLocalRef = null;
    let minuteLocalRef = null;
    for (const wdUtcStr of weekdaysUtc) {
      const wdUtc = Number(wdUtcStr); // 0..6
      const delta = (wdUtc - todayUtc.getUTCDay() + 7) % 7;
      // Build a UTC date for the next occurrence, then read local.
      const refUtc = new Date(Date.UTC(
          todayUtc.getUTCFullYear(),
          todayUtc.getUTCMonth(),
          todayUtc.getUTCDate() + delta,
          hourUtc,
          minuteUtc,
          0,
          0,
      ));
      const localWd = refUtc.getDay(); // local 0..6
      mappedLocalWeekdays.add(String(localWd));
      const mL = refUtc.getMinutes();
      const hL = refUtc.getHours();
      if (hourLocalRef === null) hourLocalRef = hL;
      if (minuteLocalRef === null) minuteLocalRef = mL;
    }
    const wdFieldLocal = Array.from(mappedLocalWeekdays).sort((a, b) => Number(a) - Number(b)).join(',');
    return [String(minuteLocalRef ?? 0), String(hourLocalRef ?? 0), '*', mo, wdFieldLocal].join(' ');
  }

  // Monthly (numeric day only)
  if (d !== '*' && d !== 'L') {
    const minuteUtc = Number(m);
    const hourUtc = Number(h);
    const dayUtc = Number(d);
    const refUtc = new Date(Date.UTC(
        todayUtc.getUTCFullYear(),
        todayUtc.getUTCMonth(),
        dayUtc,
        hourUtc,
        minuteUtc,
        0,
        0,
    ));
    const mLocal = refUtc.getMinutes();
    const hLocal = refUtc.getHours();
    const dayLocal = refUtc.getDate();
    return [String(mLocal), String(hLocal), String(dayLocal), mo, '*'].join(' ');
  }

  // "L" or anything else: pass-through
  return utcCron;
}
